import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUserArchetype } from '../hooks/useUserArchetype';
import useAuthStore from '../store/authStore';

const ArchetypeContext = createContext();

export const useArchetype = () => {
    const context = useContext(ArchetypeContext);
    if (!context) {
        throw new Error('useArchetype must be used within an ArchetypeProvider');
    }
    return context;
};

export const ArchetypeProvider = ({ children }) => {
    const { user } = useAuthStore();

    const {
        archetype,
        loading,
        error,
        hasArchetype,
        initialized,
        refreshArchetype,
        getArchetypeColor,
        getRecommendations
    } = useUserArchetype();

    const [showQuiz, setShowQuiz] = useState(false);
    const [quizCompleted, setQuizCompleted] = useState(false);

    // Verificar si el usuario necesita completar el cuestionario
    useEffect(() => {
        // Solo verificar si hay un usuario logueado
        if (!user?.userId) {
            setShowQuiz(false);
            return;
        }

        // Solo mostrar el quiz si:
        // 1. La carga inicial terminó (initialized = true)
        // 2. No está cargando actualmente
        // 3. No tiene arquetipo (backend confirmó que no existe)
        // 4. No se completó recientemente
        // 5. No hay error (para evitar mostrar en caso de error de red)
        if (initialized && !loading && !hasArchetype && !quizCompleted && !error) {
            setShowQuiz(true);
        } else {
            setShowQuiz(false);
        }
    }, [user, loading, hasArchetype, quizCompleted, error, initialized]);

    // Manejar completado del cuestionario
    const handleQuizCompleted = async () => {
        setQuizCompleted(true);
        setShowQuiz(false);
        // Refrescar el arquetipo después de un breve delay para asegurar que el backend lo haya procesado
        setTimeout(() => {
            refreshArchetype();
        }, 1000);
    };

    // Cerrar el cuestionario
    const closeQuiz = () => {
        setShowQuiz(false);
    };

    // Abrir el cuestionario manualmente
    const openQuiz = () => {
        setShowQuiz(true);
    };

    const value = {
        // Estado interno (no se muestra al usuario)
        archetype,
        loading,
        error,
        hasArchetype,
        showQuiz,
        quizCompleted,

        // Acciones
        refreshArchetype,
        handleQuizCompleted,
        closeQuiz,
        openQuiz,

        // Utilidades internas
        getArchetypeColor,
        getRecommendations
    };

    return (
        <ArchetypeContext.Provider value={value}>
            {children}
        </ArchetypeContext.Provider>
    );
}; 